import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useMyPortalAuthOptional } from '../myportal/hooks/useMyPortalAuth';

/**
 * Sind die Firmendaten des Betriebs vollständig — und darf dieser Benutzer sie ergänzen?
 *
 * Die Regel steht im Server (`Service/Company/FirmendatenPflicht`); hier wird sie nur abgefragt.
 *
 * 🚨 **Umleiten, nicht sperren.** Ben, 28.08.2026: „es soll nur die UI gesperrt werden und man
 * wird immer zu den Unternehmensinformationen hingeleitet bis alle Daten ausgefüllt wurden …
 * aber es soll nichts gesperrt werden." Die API bleibt offen; nur die Oberfläche führt zurück
 * zur Firmenmaske.
 *
 * 🚨 **Der Zustand ist GETEILT, nicht pro Hook-Instanz.** Genau daran ist die erste Fassung
 * gescheitert (29.08.2026, von Ben gemeldet): Das `Layout` und die Firmenmaske benutzen den Hook
 * beide, aber als **zwei getrennte Instanzen**. Nach dem Speichern lud nur die Maske neu — das
 * Layout behielt „unvollständig" und leitete bei jedem Klick zurück. Der Anwender war
 * **dauerhaft gefangen**, bis er die Seite neu lud. Deshalb liegen Stand und Ladezustand hier im
 * Modul, und `neuLaden()` benachrichtigt **alle** Abonnenten.
 *
 * 🚨 **Zwei Auth-Wege.** Der Hook läuft in BIS ERP *und* im Kundencenter. Dort liegt der Token
 * nicht dort, wo `api` ihn sucht — die Abfrage liefe ins Leere, der Hook fiele auf „vollständig"
 * zurück und die Umleitung wäre wirkungslos, ohne dass irgendetwas rot wird.
 *
 * 🚨 **Solange die Antwort fehlt, wird NICHT umgeleitet.** Ein Umleiten im Ladezustand risse den
 * Anwender aus der Seite, die er gerade geöffnet hat; ein Fehlschlag gilt als „vollständig" —
 * ein klemmender Endpunkt darf niemanden in eine Einstellungsmaske zwingen.
 */
export interface FirmendatenStand {
  vollstaendig: boolean;
  fehlend: string[];
  fehlendKlartext: string[];
  darfPflegen: boolean;
  zielUrl: string;
  /** Sitzland (ISO-2) oder null. */
  land: string | null;
  /**
   * Hat die Gewinnermittlung in diesem Land eine Wirkung?
   *
   * 🚨 Sie steuert genau zwei Berichte — EÜR und Anlage EKS, beide deutsch. Einem
   * niederländischen Betrieb die Auswahl hinzustellen, ohne dass ein Bericht darauf reagiert,
   * wäre eine Attrappe. Serverseitige Liste: `Rechtsform::GEWINNERMITTLUNG_LAENDER`.
   */
  gewinnermittlungRelevant: boolean;
}

const ANNAHME_OK: FirmendatenStand = {
  vollstaendig: true,
  fehlend: [],
  fehlendKlartext: [],
  darfPflegen: false,
  zielUrl: '/settings?category=general&section=company',
  land: null,
  // 🚨 Im Zweifel zeigen: Ein deutscher Betrieb darf das Feld nicht verlieren, nur weil die
  // Antwort ausbleibt.
  gewinnermittlungRelevant: true,
};

const PFAD = '/firmendaten/vollstaendigkeit';

// ---------------------------------------------------------------- geteilter Zustand
let gemeinsamerStand: FirmendatenStand = ANNAHME_OK;
let gemeinsamGeladen = false;
let laeuft: Promise<void> | null = null;
const abonnenten = new Set<() => void>();

function melden() {
  for (const fn of abonnenten) fn();
}

// V-1659: Der Stand gehoert zur Sitzung — beim Abmelden/Benutzer-/Mandantenwechsel zuruecksetzen.
api.onAuthWechsel(() => {
  gemeinsamerStand = ANNAHME_OK;
  gemeinsamGeladen = false;
  laeuft = null;
  melden();
});

/**
 * Holt den Stand — höchstens ein Aufruf gleichzeitig.
 *
 * `erzwingen` umgeht den „schon geladen"-Kurzschluss; das braucht der Speichern-Vorgang.
 */
async function holen(
  portalFetch: ((p: string, o?: RequestInit) => Promise<Response>) | undefined,
  erzwingen: boolean,
): Promise<void> {
  if (laeuft) return laeuft;
  if (gemeinsamGeladen && !erzwingen) return;

  laeuft = (async () => {
    try {
      let ergebnis: FirmendatenStand | null = null;
      if (portalFetch) {
        // 🚨 `cache: 'no-store'` beim Erzwingen: Der Endpunkt trägt `max-age=30`. Ohne das
        // beantwortet der Browser die Nachfrage direkt nach dem Speichern aus seinem eigenen
        // Zwischenspeicher — mit dem Stand von vorher.
        const res = await portalFetch(PFAD, erzwingen ? { cache: 'no-store' } : {});
        if (res.ok) ergebnis = await res.json() as FirmendatenStand;
      } else {
        ergebnis = await api.get<FirmendatenStand>(erzwingen ? `${PFAD}?t=${Date.now()}` : PFAD);
      }
      if (ergebnis) gemeinsamerStand = { ...ANNAHME_OK, ...ergebnis };
    } catch {
      // Kein Alarm: fail-open, siehe Kommentar oben.
    } finally {
      gemeinsamGeladen = true;
      laeuft = null;
      melden();
    }
  })();

  return laeuft;
}

export function useFirmendatenPflicht() {
  const portal = useMyPortalAuthOptional();
  const portalFetch = portal?.myPortalFetch;
  const [, neuZeichnen] = useState(0);

  useEffect(() => {
    const anstossen = () => neuZeichnen((n) => n + 1);
    abonnenten.add(anstossen);
    void holen(portalFetch, false);
    return () => { abonnenten.delete(anstossen); };
  }, [portalFetch]);

  /** Nach dem Speichern: erzwingt eine frische Antwort für ALLE Abonnenten. */
  const neuLaden = useCallback(() => {
    void holen(portalFetch, true);
  }, [portalFetch]);

  const stand = gemeinsamerStand;
  const geladen = gemeinsamGeladen;

  return {
    stand,
    geladen,
    /** Umleiten? Nur wer speichern darf, wird geführt — sonst wäre es eine Sackgasse. */
    mussPflegen: geladen && !stand.vollstaendig && stand.darfPflegen,
    /** Für alle anderen: nur ein Hinweis. */
    zeigeHinweis: geladen && !stand.vollstaendig && !stand.darfPflegen,
    neuLaden,
  };
}
